// The outbound mail log. Admin-only on the server: a row names a client
// and what the firm wrote to them.

import { getAuthHeaders } from "./auth";

export const MAIL_KINDS = [
  "invitation",
  "otp",
  "invoice",
  "digest",
  "alert",
  "auth",
  "other",
] as const;
export type MailKind = (typeof MAIL_KINDS)[number];

export interface EmailDeliveryRow {
  _id: string;
  to: string;
  subject: string;
  kind: MailKind;
  status: "queued" | "sent" | "failed" | "skipped";
  caseId: string | null;
  /** The matter's public id - what every link in the app is built on. */
  caseEId: string | null;
  /** Why it did not go: the provider's first line, or the gate that refused. */
  error: string | null;
  /** Tries so far. A queued row is waiting for the next one. */
  attempts: number;
  createdAt: string;
}

export interface EmailDeliveryPage {
  deliveries: EmailDeliveryRow[];
  total: number;
  page: number;
  totalPages: number;
}

export async function fetchEmailDeliveries(params: {
  kind?: MailKind | "";
  status?: string;
  page?: number;
}): Promise<EmailDeliveryPage> {
  const query = new URLSearchParams();
  if (params.kind) query.set("kind", params.kind);
  if (params.status) query.set("status", params.status);
  if (params.page && params.page > 1) query.set("page", String(params.page));

  const res = await fetch(`/api/admin/email-deliveries?${query.toString()}`, {
    headers: { ...getAuthHeaders() },
    credentials: "same-origin",
  });
  if (!res.ok) throw new Error(`GET email deliveries failed: ${res.status}`);
  return (await res.json()) as EmailDeliveryPage;
}

/** Queued rows survive the clear - they are messages still going out. */
export async function clearEmailDeliveries(): Promise<number> {
  const res = await fetch("/api/admin/email-deliveries", {
    method: "DELETE",
    headers: { ...getAuthHeaders() },
    credentials: "same-origin",
  });
  if (!res.ok) throw new Error(`Clearing the email log failed: ${res.status}`);
  return ((await res.json()) as { removed: number }).removed;
}
